import React from 'react';
import Eyebrow from './Eyebrow';
import Heading from './Heading';
import Text from './Text';
import { cn } from '@/lib/twUtils';
import type { HeadingProps } from './Heading';

/**
 * Larghezza per le testate di sezione lunghe: misurata perché i titoli in uso
 * stiano su due righe e mai su tre. Il default di `SectionIntro` è più
 * stretto e va bene per i titoli brevi.
 */
export const WIDE_HEADING = 'max-w-[58rem]';

export interface SectionIntroProps {
  eyebrow?: string;
  title: React.ReactNode;
  text?: React.ReactNode;
  /** Dimensione del titolo, default `lg` */
  size?: HeadingProps['size'];
  as?: HeadingProps['as'];
  /** `light` = su fondo chiaro, `dark` = su banda navy */
  tone?: 'light' | 'dark';
  align?: 'start' | 'center';
  /** CTA o link allineato a destra del titolo (solo desktop) */
  action?: React.ReactNode;
  className?: string;
  /** Larghezza massima del blocco di testo */
  maxWidth?: string;
}

/**
 * Testata di sezione: occhiello, titolo e introduzione con le stesse
 * proporzioni ovunque. `action` mette una CTA in linea sul lato destro.
 */
const SectionIntro = ({
  eyebrow,
  title,
  text,
  size = 'lg',
  as = 'h2',
  tone = 'light',
  align = 'start',
  action,
  className,
  maxWidth = 'max-w-[41.25rem]',
}: SectionIntroProps) => {
  const block = (
    <div className={cn(maxWidth, align === 'center' && 'mx-auto text-center')}>
      {eyebrow && (
        <Eyebrow tone={tone === 'dark' ? 'accent' : 'accent'} className="mb-3.5">
          {eyebrow}
        </Eyebrow>
      )}
      <Heading as={as} size={size} tone={tone === 'dark' ? 'light' : 'ink'}>
        {title}
      </Heading>
      {text && (
        <Text
          size="intro"
          tone={tone === 'dark' ? 'onDark' : 'muted'}
          className={cn('mt-4.5 max-w-[36ch] sm:max-w-[58ch]', align === 'center' && 'mx-auto')}
        >
          {text}
        </Text>
      )}
    </div>
  );

  if (!action) return <div className={className}>{block}</div>;

  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-6', className)}>
      {block}
      <div className="shrink-0">{action}</div>
    </div>
  );
};

export default SectionIntro;
